import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { 
  Wallet, Target, TrendingUp, 
  Users, Gift, ChevronRight, ChevronLeft, Sparkles
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

const parentSteps = [
  {
    icon: Sparkles,
    title: "Welcome to CoinQuest!",
    description: "Help your child develop healthy money habits through fun, gamified learning!",
    color: "from-[#FFD23F] to-[#FFEB99]"
  },
  {
    icon: Users,
    title: "Connect with Your Child",
    description: "Link your account to your child's profile to monitor their progress and activities.",
    color: "from-[#3D5A80] to-[#5A7CA8]"
  },
  {
    icon: Target,
    title: "Create Chores & Rewards",
    description: "Assign chores with XP rewards. Approve completed chores to teach the value of earning.",
    color: "from-[#06D6A0] to-[#42E8B3]"
  },
  {
    icon: Wallet,
    title: "Manage Allowances",
    description: "Set up recurring allowances and one-time rewards. You can also apply penalties for learning.",
    color: "from-[#EE6C4D] to-[#FF9F7F]"
  },
  {
    icon: Gift,
    title: "Give Money & Gifts",
    description: "Transfer money or XP directly to your child's wallet for special occasions or as rewards!",
    color: "from-[#9B5DE5] to-[#C77DFF]"
  },
  {
    icon: TrendingUp,
    title: "Track Progress",
    description: "View detailed insights about your child's spending, saving, and learning activities.",
    color: "from-[#4CC9F0] to-[#72EFDD]"
  }
];

export default function OnboardingTour({ user, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  
  // Children no longer get the onboarding tour popup — only parents do.
  const shouldShowOnboarding = user && 
    user.role === 'parent' && 
    !user.has_completed_onboarding;
  
  const [isOpen, setIsOpen] = useState(shouldShowOnboarding);
  
  const steps = parentSteps;
  const isLastStep = currentStep === steps.length - 1;
  
  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const handlePrev = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };
  
  const handleComplete = async () => {
    try {
      await axios.post(`${API}/auth/complete-onboarding`);
    } catch (error) {
      console.error('Failed to save onboarding status:', error);
    }
    setIsOpen(false);
    if (onComplete) onComplete();
  };
  
  const handleSkip = async () => {
    await handleComplete();
  };
  
  if (!isOpen) return null;
  
  const step = steps[currentStep];
  const StepIcon = step.icon;
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleSkip(); }}>
      <DialogContent 
        className="bg-white border-2 border-[#1D3557] rounded-2xl max-w-md p-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Header with icon */}
        <div className={`bg-gradient-to-r ${step.color} px-6 py-5`}>
          <div className="flex items-center gap-4">
            <div className="bg-white/30 w-12 h-12 rounded-xl flex items-center justify-center">
              <StepIcon className="w-6 h-6 text-[#1D3557]" />
            </div>
            <h2 
              className="text-xl font-bold text-[#1D3557]" 
              style={{ fontFamily: 'Fredoka' }}
              data-testid="onboarding-title"
            >
              {step.title}
            </h2>
          </div>
        </div>
        
        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-[#3D5A80] text-base leading-relaxed">
            {step.description}
          </p>
          
          {/* Progress dots */}
          <div className="flex justify-center gap-2 my-4">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentStep 
                    ? 'bg-[#FFD23F] scale-125' 
                    : index < currentStep 
                      ? 'bg-[#06D6A0]' 
                      : 'bg-gray-300'
                }`}
                data-testid={`onboarding-dot-${index}`}
              />
            ))}
          </div>
          
          {/* Navigation buttons */}
          <div className="flex gap-3">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`flex items-center gap-1 px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                currentStep === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-[#E0FBFC] text-[#3D5A80] hover:bg-[#98C1D9]'
              }`}
              data-testid="onboarding-prev"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            
            <button
              onClick={handleNext}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-white transition-all ${
                isLastStep 
                  ? 'bg-[#06D6A0] hover:bg-[#05B88A]' 
                  : 'bg-[#3D5A80] hover:bg-[#1D3557]'
              }`}
              data-testid="onboarding-next"
            >
              {isLastStep ? (
                <>
                  Let&apos;s Go! <Sparkles className="w-4 h-4" />
                </>
              ) : (
                <>
                  Next <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
          
          {/* Step counter */}
          <p className="text-center text-xs text-gray-400 mt-3">
            {currentStep + 1} of {steps.length}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
